import { useState } from "react";
import { Link } from "react-router-dom";
import { Twitter, Instagram, Youtube } from "lucide-react";
import { useLocale } from "../../context/LocaleContext";
import { Logo } from "../Logo";
import { LEGAL_SLUGS } from "../../pages/legal/legalContent";

const EXPLORE = ["nutrition", "fitness", "recipes", "exercises", "body", "sports", "sleep", "weight"];

export function Footer() {
  const { sections, locales, changeLocale, locale, t, lp, sp } = useLocale();
  const byslug = Object.fromEntries(sections.map((s) => [s.slug, s]));
  const explore = EXPLORE.map((s) => byslug[s]).filter(Boolean);

  const col = (title, links) => (
    <div>
      <h4 className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{title}</h4>
      <ul className="mt-4 space-y-2.5">
        {links.map((l, i) => (
          <li key={`${title}-${i}`}>
            <Link to={l.to} className="text-sm text-slate-600 transition-colors hover:text-emerald-700">{l.label}</Link>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer className="mt-24 border-t border-slate-200 bg-slate-50">
      <div className="container-page py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2"><Logo className="h-8" alt="LIVYVIO" /></div>
            <p className="mt-3 max-w-xs text-sm leading-relaxed text-slate-500">{t("footerTagline")}</p>
            <div className="mt-5 flex gap-2">
              {[Twitter, Instagram, Youtube].map((Ico, i) => (
                <span key={i} aria-hidden className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400"><Ico className="h-4 w-4" /></span>
              ))}
            </div>
            <div className="mt-5">
              <select value={locale} onChange={(e) => changeLocale(e.target.value)} data-testid="footer-locale"
                className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-600">
                {locales.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
          </div>
          {col(t("footerExplore"), explore.map((s) => ({ to: lp(`/${s.path || s.slug}`), label: s.title })))}
          {col(t("footerGoals"), [{ to: lp(`/${sp("goals")}`), label: t("navGoals") }, { to: lp(`/${sp("plans")}`), label: byslug.plans?.title || "Plans" }])}
          {col(t("footerLearn"), [{ to: lp(`/${sp("articles")}`), label: t("navLearn") }, { to: lp(`/${sp("calculators")}`), label: t("navTools") }, { to: lp(`/${sp("products")}`), label: t("navProducts") }])}
          {col(t("footerCompany"), [
            { to: lp(`/${sp("about")}`), label: t("about") },
            { to: lp(`/${sp("about")}`), label: t("contact") },
            { to: lp(`/${LEGAL_SLUGS.privacy[locale]}`), label: t("privacy") },
            { to: lp(`/${LEGAL_SLUGS.cookies[locale]}`), label: t("cookies") },
            { to: lp(`/${LEGAL_SLUGS.terms[locale]}`), label: t("terms") },
          ])}
        </div>

        <div className="mt-12 space-y-4 border-t border-slate-200 pt-6">
          <p className="text-xs leading-relaxed text-slate-400">
            <strong className="text-slate-500">{t("healthDisclaimer")}:</strong> {t("footerTagline")}{" "}
            <strong className="text-slate-500">{t("affiliateDisclosure")}:</strong> {t("externalNote")}
          </p>
          <div className="flex flex-col gap-2 text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between">
            <span>© {new Date().getFullYear()} LIVYVIO. All rights reserved.</span>
            <span>{t("footerLangs")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
